'use no memo';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type {
	useTitleContentPropertiesForPublishedPageQuery$data,
	useTitleContentPropertiesForPublishedPageQuery$variables,
} from './__generated__/useTitleContentPropertiesForPublishedPageQuery.graphql';
import { defaultPropertyValue, COVERPICTUREWIDTH } from './contentTopperConstants';
import { contentPropertyHelper } from './contentPropertyHelper';

const UseTitleContentPropertiesForPublishedPageQuery = graphql`
	query useTitleContentPropertiesForPublishedPageQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				pageTitlePublished: properties(key: "page-title-property-published") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				pageTitleDraft: properties(key: "page-title-property-draft") {
					nodes {
						id
						key
						value
						version {
							number
						}
					}
				}
				metadata {
					frontend {
						coverPictureWidth
					}
				}
			}
		}
	}
`;

export const PreloadHomepageIdQuery = graphql`
	query useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			homepage {
				id
			}
		}
	}
`;

type useTitleContentPropertiesForPublishedPageProps = {
	contentId?: string;
};

export const getTitleContentProperty = (data: any) => {
	const titlePropertyContent = data?.content?.nodes?.[0]?.pageTitlePublished?.nodes?.find(
		(node: any) => node?.key == 'page-title-property-published',
	);
	const coverPictureWidth = (data?.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth ||
		COVERPICTUREWIDTH.FULL) as COVERPICTUREWIDTH;

	if (titlePropertyContent?.value) {
		try {
			return {
				titleContentProperties: contentPropertyHelper(titlePropertyContent),
				coverPictureWidth,
			};
		} catch (e) {
			return { titleContentProperties: defaultPropertyValue, coverPictureWidth };
		}
	} else {
		return { titleContentProperties: defaultPropertyValue, coverPictureWidth };
	}
};

export const useTitleContentPropertiesForPublishedPage = ({
	contentId,
}: useTitleContentPropertiesForPublishedPageProps) => {
	const { data, error, loading } = useQueryInterop<
		useTitleContentPropertiesForPublishedPageQuery$data,
		useTitleContentPropertiesForPublishedPageQuery$variables
	>(UseTitleContentPropertiesForPublishedPageQuery, {
		variables: {
			contentId,
			status: ['current', 'archived', 'draft'],
		},
		fetchPolicy: 'cache-first',
	});

	if (!loading && data && !error) {
		const { titleContentProperties, coverPictureWidth } = getTitleContentProperty(data);

		return { titleContentProperties, coverPictureWidth };
	}

	return {
		titleContentProperties: defaultPropertyValue,
		coverPictureWidth: COVERPICTUREWIDTH.FULL,
	};
};
